import {EventEmitter, Injectable, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {TimeSeriesPerformanceStoreService} from "../time-series-performance-store.service";
import {CombinedOrderElementDto} from "../../models/core/combined-order-element-dto";
import {MonthRow} from "../../components/tables/event-overview-table/models/month-row";
import {TimeRepository} from "../../repositories/core/time/time-repository";
import {EmploymentCostRepository} from "../../repositories/core/employment-cost/employment-cost-repository";
import {EventRepository} from "../../repositories/core/event/event-repository";
import {OrderRepository} from "../../repositories/core/orders/order-repository";
import {LinkedDtoRepository} from "../../../common/repositories/linked-dto-repository";
import {EventRow} from "../../components/tables/event-overview-table/models/event-row";


@Injectable(
)
export class EventTableStore implements OnDestroy {
  subscription: Subscription = new Subscription()
  isRegistered: boolean = false


  revenuePerMonthTableData: Map<string, MonthRow<CombinedOrderElementDto>> = new Map<string, MonthRow<CombinedOrderElementDto>>()

  updateNotifier: EventEmitter<any> = new EventEmitter<any>()

  constructor(
    private timeSeriesPerformanceStoreService: TimeSeriesPerformanceStoreService,
    public timeRepository: TimeRepository,
    private eventRepository: EventRepository,
    private employmentCostRepository: EmploymentCostRepository,
    private orderRepository: OrderRepository,
    private linkedDtoRepository: LinkedDtoRepository<any>
  ) {
    this.register()
  }

  register() {
    if (this.isRegistered)
      return
    this.isRegistered = true
    this.subscription.add(this.timeSeriesPerformanceStoreService.updateNotifier.subscribe(() => {
        this.update()
      }
    ))
    this.subscription.add(this.timeSeriesPerformanceStoreService.initializationCompletedNotifier.subscribe(() => {
        this.initializeTableData()
      }
    ))

  }

  initializeTableData() {
    this.revenuePerMonthTableData = this.createTableDataByMonth()
  }

  update() {
    this.processTableData(this.timeSeriesPerformanceStoreService.isEmployeeCostsIncluded, this.timeSeriesPerformanceStoreService.revenueFn)
    this.updateNotifier.emit()
  }

  createTableDataByMonth(): Map<string, MonthRow<CombinedOrderElementDto>> {
    let tableDataMap: Map<string, MonthRow<CombinedOrderElementDto>> = new Map<string, MonthRow<CombinedOrderElementDto>>()

    let months = Array.from(this.timeRepository.getMonthNameMap().values())
    for (let month of months) {
      let row = new MonthRow<CombinedOrderElementDto>()
      row.name = month.name
      tableDataMap.set(month.name, row)
    }

    for (let event of this.eventRepository.getEvents()) {
      let interval = this.timeRepository.toDateString(event.start)//this.timeRepository.toKey(event.start, event.id)
      let eventRow = new EventRow<CombinedOrderElementDto>()
      eventRow.eventDto = event
      eventRow.employeeCosts = this.employmentCostRepository.getCost(event.id)
      tableDataMap.get(interval)!.eventEntries.push(eventRow)
    }

    return tableDataMap
  }


  processTableData(includeEmployeeCost: boolean, revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number) {
    // clear tableData
    for (let monthRow of this.revenuePerMonthTableData.values()) {
      for (let eventRow of this.revenuePerMonthTableData.get(monthRow.name)!.eventEntries) {
        eventRow.orders = []
      }
    }


    for (let combinedOrderElement of this.orderRepository.getActiveOrderElements()) {
      let interval = this.timeRepository.getAxisNameMapTimeInterval(combinedOrderElement.interval)

      // Todo optimize this fragment
      let eventRow = this.revenuePerMonthTableData.get(interval)!.eventEntries.find(eventRow => eventRow.eventDto.id === combinedOrderElement.eventId)
      eventRow!.orders.push(combinedOrderElement)
    }

    //preprocess revenue of each month
    for (let monthRow of this.revenuePerMonthTableData.values()) {
      let monthRevenue = 0
      for (let eventRow of monthRow.eventEntries) {
        let displayedEventRevenue = 0
        let eventRevenue = 0
        for (let combinedOrderElement of eventRow.orders) {
          displayedEventRevenue += revenueFn(combinedOrderElement)

        }

        if (includeEmployeeCost && eventRow.isActive)
          displayedEventRevenue -= eventRow.employeeCosts
        eventRow.displayedRevenue = displayedEventRevenue
        monthRevenue += displayedEventRevenue
      }
      monthRow.monthRevenue = monthRevenue
    }
  }


  calculateRevenueOfEventRow(eventRow: EventRow<CombinedOrderElementDto>, categoryKey: keyof CombinedOrderElementDto): {
    id: string,
    name: string,
    revenue: number
  }[] {
    let map: Map<string, { id: string, name: string, revenue: number }> = new Map<string, {
      id: string,
      name: string,
      revenue: number
    }>()
    for (let combinedOrderElement of eventRow.orders) {
      let key = combinedOrderElement[categoryKey] as string
      if (map.has(key)) {
        map.get(key)!.revenue += this.timeSeriesPerformanceStoreService.revenueFn(combinedOrderElement)
      } else {
        map.set(key, {
          id: key,
          name: this.linkedDtoRepository.getName(key, categoryKey),
          revenue: this.timeSeriesPerformanceStoreService.revenueFn(combinedOrderElement)
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => b.name.localeCompare(a.name))
  }

  calculateRevenueOfCombinedOrderElementList(combinedOrderElementList: CombinedOrderElementDto[], revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number): number {
    return combinedOrderElementList.reduce((acc, cur) => acc + revenueFn(cur), 0)
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

}
